/**
 * The HELP listing, derived (D43, D229): the loaded game → the directive
 * surface a player can actually use IN THIS GAME.
 *
 * @remarks
 * The built-in `help` line used to write this game's verbs down by hand, which
 * made it a help screen that lies in any game whose vocabulary differs — and
 * after D35 declared eighteen unsupported verbs, it was already lying in this
 * one (Calder's Lane A finding). Deriving it means HELP cannot drift from the
 * vocabulary, because there is nothing left to drift.
 *
 * What it says is what the grammar will accept AND the world will answer
 * (D229, E15 — the play-test found `equip`, `unequip`, `open`, `close` and
 * `use` listed in a game with nothing to wear, no door and no interaction,
 * and the pack's own `put` and `seal` hidden behind `use`):
 *
 * - **Unsupported intents are omitted.** D35 recognizes them so they can be
 *   refused by name; a listing of things that do not work is not help.
 * - **Directives the game cannot answer are omitted** (`directive-affordances`):
 *   a verb whose every action has nothing in this pack to act on would only
 *   refuse, and a listing of refusals is not help either.
 * - **The pack's own words for a verb stand beside its token** — `use, put,
 *   seal <thing>` — because they are the game's design vocabulary, where the
 *   base's aliases are conveniences the listing has never spelled out.
 * - **Object shape comes from `objectPolicy`** — `required` renders bare,
 *   `optional` renders bracketed, `forbidden` renders nothing.
 * - **Prepositions come from the intent's DECLARED bindings**, one per role in
 *   declaration order, so `use` shows its target and its tool without listing
 *   every synonym for either.
 * - **The movement intent is asked for by DECLARATION** (`movementIntentToken`),
 *   never by matching the word "go", and renders `<direction>` because a
 *   direction is the answer that works — the engine's own `missingDirection`
 *   slot exists to say so. `movementIntentToken` is optional, and a vocabulary
 *   that omits it still moves: `compileLexicon` synthesizes a movement record
 *   that is deliberately NOT in `intentsByToken` (its token contains a `:`, so
 *   no player can type it). Walking the intent map alone would therefore drop
 *   movement from HELP entirely — the one thing a bare direction does — so
 *   that case renders the bare `<direction>` form instead.
 *
 * Pure and deterministic: same lexicon, same string. Map iteration order is
 * declaration order and the intent list is sorted, so nothing here depends on
 * insertion luck.
 *
 * @packageDocumentation
 */

import type { CompiledIntent } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { gameAnswersDirective } from '../actions/directive-affordances.js';

/** How a noun slot is shown to the player. */
const OBJECT_PLACEHOLDER = '<thing>';

/**
 * How the movement intent's argument is shown.
 *
 * @remarks
 * The angle-bracket convention is shared with the built-in response texts
 * (`verbNeedsTarget`, `questionNotUnderstood`) and with pack overrides, which
 * are authored JSON no constant can reach. It stays a literal there on
 * purpose; this constant exists for the code that BUILDS strings, not for the
 * prose that contains them.
 */
const DIRECTION_PLACEHOLDER = '<direction>';

/** What separates one directive from the next. */
const DIRECTIVE_SEPARATOR = ' / ';

/** What separates a verb's token from the words the pack teaches for it (D229): `use, put, seal <thing>`. */
const PACK_WORD_SEPARATOR = ', ';

/**
 * Render the directive listing for one loaded game.
 *
 * @param bundle - The loaded game: its compiled vocabulary (the single source of verb truth), its pack (what the verbs can act on) and the words the pack teaches
 * @param withheld - Canonical tokens NOT to list — directives that exist in this vocabulary but do nothing in this game (D179: `save` / `load` on a pack that saves itself). A HELP that advertises a directive which then declines is the promises law broken by the chrome.
 * @returns The listing, e.g. `look / open <thing> [with <thing>]`
 */
export const renderDirectiveListing = (bundle: EngineGameBundle, withheld: readonly string[] = []): string => {
	const lexicon = bundle.compiledLexicon;
	const movementIntentToken = lexicon.movementIntent.canonicalToken;
	// Aliases share one CompiledIntent, so dedupe by canonical token — `pick up`
	// and `take` are one directive, and the listing should say so once. D229:
	// and only the directives this game answers.
	const intentsByCanonicalToken = new Map<string, CompiledIntent>();

	for (const intent of lexicon.intentsByToken.values()) {
		const isAnswered = gameAnswersDirective(intent.canonicalToken, bundle.contentPack, movementIntentToken);

		if (!intent.isUnsupported && !withheld.includes(intent.canonicalToken) && isAnswered) {
			intentsByCanonicalToken.set(intent.canonicalToken, intent);
		}
	}

	const declaredUsages = [...intentsByCanonicalToken.values()]
		.sort((leftIntent, rightIntent) => leftIntent.canonicalToken.localeCompare(rightIntent.canonicalToken, 'en'))
		.map((intent) => renderDirectiveUsage(intent, movementIntentToken, bundle.indexes.packVerbWords.get(intent.canonicalToken) ?? []));

	// The synthesized movement record is not in the intent map, so nothing above
	// rendered it. Movement still WORKS (a bare direction parses), and dropping
	// it from HELP would hide the most basic directive in the game — so it leads
	// the listing in the form the player can actually type.
	if (!intentsByCanonicalToken.has(movementIntentToken)) {
		return [DIRECTION_PLACEHOLDER, ...declaredUsages].join(DIRECTIVE_SEPARATOR);
	}

	return declaredUsages.join(DIRECTIVE_SEPARATOR);
};

/**
 * Render one intent's usage line.
 *
 * @param intent - The compiled intent
 * @param movementIntentToken - The vocabulary's declared movement intent
 * @param packWords - D229: the words the pack teaches for this verb beyond its token, listed beside it
 * @returns The usage string for this directive
 */
const renderDirectiveUsage = (intent: CompiledIntent, movementIntentToken: string, packWords: readonly string[]): string => {
	const verbWords = [intent.canonicalToken, ...packWords].join(PACK_WORD_SEPARATOR);

	if (intent.canonicalToken === movementIntentToken) {
		return `${verbWords} ${DIRECTION_PLACEHOLDER}`;
	}

	const usageParts: string[] = [verbWords];
	// D229: a preposition that introduces the object is optional before it — `cut [at] <thing>`.
	const { bound, introducer } = prepositionsByUse(intent.prepositionRoles);
	const objectPlaceholder = introducer === undefined ? OBJECT_PLACEHOLDER : `[${introducer}] ${OBJECT_PLACEHOLDER}`;

	if (intent.objectPolicy === 'required') {
		usageParts.push(objectPlaceholder);
	} else if (intent.objectPolicy === 'optional') {
		usageParts.push(`[${objectPlaceholder}]`);
	}

	for (const preposition of bound) {
		usageParts.push(`[${preposition} ${OBJECT_PLACEHOLDER}]`);
	}

	return usageParts.join(' ');
};

/**
 * The first preposition declared for each role, in declaration order — the
 * one that introduces the object (D229) apart from the ones that bind.
 *
 * @remarks
 * `open` binds both `with` and `using` to the tool role; the listing teaches
 * one phrasing rather than enumerating synonyms, and the parser accepts every
 * one of them regardless.
 *
 * @param prepositionRoles - The intent's declared preposition → role map
 * @returns The object's introducer, if any, and the representative bound prepositions in role order
 */
const prepositionsByUse = (prepositionRoles: CompiledIntent['prepositionRoles']): { readonly bound: readonly string[]; readonly introducer: string | undefined } => {
	const prepositionByRole = new Map<string, string>();

	for (const [preposition, role] of prepositionRoles) {
		if (!prepositionByRole.has(role)) {
			prepositionByRole.set(role, preposition);
		}
	}

	const introducer = prepositionByRole.get('direct_object');

	prepositionByRole.delete('direct_object');

	return { bound: [...prepositionByRole.values()], introducer };
};
